import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import '../styles/qa-theme.css';
import ChatPanelDifyChatflowApi from '../../chat/ChatPanelDifyChatflowApi';
import DocumentManage from '../../features/document/DocumentManage';
import { QuestionCircleOutlined, RobotOutlined, FolderOpenOutlined, StarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import permissionManager from '../../utils/permissions';
import axios from '../../api';
import theme from '../../theme';
import LanguageSelector from '../../components/LanguageSelector';
import AgentAvatar from '../../components/AgentAvatar';
import AgentSidebar from '../components/AgentSidebar';

// 缓存配置
const CACHE_CONFIG = {
  AGENT_LIST: {
    key: 'school_dashboard_agent_list',
    ttl: 5 * 60 * 1000, // 5分钟缓存
  },
  USER_INFO: {
    key: 'school_dashboard_user_info',
    ttl: 10 * 60 * 1000, // 10分钟缓存
  },
  PERMISSIONS: {
    key: 'school_dashboard_permissions',
    ttl: 15 * 60 * 1000, // 15分钟缓存
  }
};

// 缓存工具函数
const cacheUtils = {
  // 设置缓存
  setCache: (key, data, ttl) => {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  },

  // 获取缓存
  getCache: (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // 检查缓存是否过期
      if (now - cacheData.timestamp > cacheData.ttl) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.error('缓存读取失败:', error);
      localStorage.removeItem(key);
      return null;
    }
  },

  // 清除缓存
  clearCache: (key) => {
    localStorage.removeItem(key);
  },

  // 清除所有相关缓存
  clearAllCache: () => {
    Object.values(CACHE_CONFIG).forEach(config => {
      localStorage.removeItem(config.key);
    });
  }
};

// 智能体聊天面板组件
const AgentChatPanel = React.memo(({
  agent,
  userInfo,
  t,
  agentSidebar,
}) => {
  if (!agent || agent.expired) {
    return null;
  }

  return (
    <ChatPanelDifyChatflowApi
      key={agent.id} // 使用智能体ID作为key，确保每个智能体有独立的组件实例
      agentList={[agent]} // 只传递当前智能体
      currentAgent={agent}
      agentId={agent.id}
      apiKey={agent.apikey}
      apiUrl={agent.api_url}
      responseMode="streaming"
      onAgentChange={() => {}} // 禁用智能体切换，因为每个面板只对应一个智能体
      welcomeText={agent.intro || agent.welcomeText || t('common.welcomeToDifyAgent')}
      style={{ height: '100%' }}
      disabled={false}
      userInfo={userInfo}
      agentSidebar={agentSidebar}
    />
  );
});

const SchoolDashboard = () => {
  const { t, i18n } = useTranslation();
  const originalAgentsRef = useRef([]); // 保存原始智能体数据
  
  const menuIcons = useMemo(() => [
    { key: 'qa', label: t('common.qa'), icon: <QuestionCircleOutlined /> },
    { key: 'robot', label: t('common.agents'), icon: <RobotOutlined /> },
    { key: 'folder', label: t('common.cloudSpace'), icon: <FolderOpenOutlined /> }
  ], [t]);

  const getAvailableMenuItems = () => menuIcons.filter(item => {
    if (item.key === 'qa') {
      return permissionManager.hasModulePermission('chat');
    }
    if (item.key === 'robot') {
      return permissionManager.hasModulePermission('agent');
    }
    if (item.key === 'folder') {
      return permissionManager.hasModulePermission('document');
    }
    return true;
  });

  const getDefaultTabKey = () => getAvailableMenuItems()[0]?.key || null;
  const [selectedKey, setSelectedKey] = useState('qa');
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [initError, setInitError] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [hasUserSelectedAgent, setHasUserSelectedAgent] = useState(false); // 标记用户是否手动选择过智能体
  const [hasVisitedRobotTab, setHasVisitedRobotTab] = useState(false); // 标记是否已经访问过智能体tab
  


  const currentAgent = agentList.find(a => a.id === currentAgentId);

  // 处理智能体点击
  const handleAgentClick = (agent) => {
    if (agent.expired) {
      // 如果是过期或无权限的智能体，显示toast提示
      if (agent.relationship_status === 'no_permission') {
        message.warning(t('common.noPermissionContactAdmin'));
      } else {
        message.warning(t('common.notOpenedContactAdmin'));
      }
      return;
    }
    
    // 有权限的智能体，正常设置
    console.log('👤 用户手动选择智能体:', agent.name, 'ID:', agent.id);
    setHasUserSelectedAgent(true); // 标记用户已手动选择
    setCurrentAgentId(agent.id);
  };

  // 处理tab切换
  const handleTabChange = (newKey) => {
    setSelectedKey(newKey);
    
    // 如果是第一次切换到智能体tab，自动选择第一个可用的智能体
    if (newKey === 'robot' && !hasVisitedRobotTab) {
      setHasVisitedRobotTab(true);
      
      // 延迟执行，确保agentList已经加载完成
      setTimeout(() => {
        // 优先选择第一个有权限的智能体，而不是所有智能体中的第一个
        const firstAvailableAgent = openedAgents.length > 0 ? openedAgents[0] : null;
        if (firstAvailableAgent && !currentAgentId) {
          console.log('🎯 第一次打开智能体tab，自动选择第一个可用智能体:', firstAvailableAgent.name);
          setCurrentAgentId(firstAvailableAgent.id);
        } else if (!firstAvailableAgent && agentList.length > 0) {
          // 如果没有可用智能体，但有智能体列表，选择第一个（可能是无权限的，但至少不会空白）
          console.log('⚠️ 没有可用智能体，选择第一个智能体作为默认显示');
          setCurrentAgentId(agentList[0].id);
        }
      }, 100);
    }
  };

  // 加载用户信息：先用本地缓存快速展示，再始终从 API 刷新（确保学号等字段及时更新）
  const loadUserInfo = async () => {
    console.log('🔄 开始加载用户信息...');

    let fallbackUserInfo = null;
    const cachedUserInfo = cacheUtils.getCache(CACHE_CONFIG.USER_INFO.key);
    if (cachedUserInfo?.id) {
      console.log('✅ 从内存缓存预加载用户信息');
      fallbackUserInfo = cachedUserInfo;
      setUserInfo(cachedUserInfo);
    } else {
      try {
        const userData = localStorage.getItem('user_info');
        if (userData) {
          const parsedUserInfo = JSON.parse(userData);
          if (parsedUserInfo?.id) {
            console.log('✅ 从 localStorage 预加载用户信息');
            fallbackUserInfo = parsedUserInfo;
            cacheUtils.setCache(CACHE_CONFIG.USER_INFO.key, parsedUserInfo, CACHE_CONFIG.USER_INFO.ttl);
            setUserInfo(parsedUserInfo);
          }
        }
      } catch (error) {
        console.warn('解析本地用户信息失败:', error);
      }
    }

    try {
      const response = await axios.get('/api/userinfo/');
      const fetchedUserInfo = response.data;
      console.log('✅ 从服务器刷新用户信息');
      localStorage.setItem('user_info', JSON.stringify(fetchedUserInfo));
      cacheUtils.setCache(CACHE_CONFIG.USER_INFO.key, fetchedUserInfo, CACHE_CONFIG.USER_INFO.ttl);
      setUserInfo(fetchedUserInfo);
      return fetchedUserInfo;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return fallbackUserInfo;
    }
  };

  // 加载权限信息（带缓存）
  const loadPermissions = async () => {
    console.log('🔄 开始加载权限信息...');

    try {
      const cachedPermissions = cacheUtils.getCache(CACHE_CONFIG.PERMISSIONS.key);
      if (cachedPermissions) {
        console.log('✅ 从缓存加载权限信息');
        permissionManager.restoreFromCache(cachedPermissions);
        return;
      }

      await permissionManager.loadPermissions();

      const permissionsData = permissionManager.getCacheData();
      if (permissionsData) {
        cacheUtils.setCache(CACHE_CONFIG.PERMISSIONS.key, permissionsData, CACHE_CONFIG.PERMISSIONS.ttl);
      }

      console.log('✅ 从服务器加载权限信息');
    } catch (error) {
      console.error('加载权限信息失败:', error);
    }
  };

  // 初始化加载
  useEffect(() => {
    const initializeData = async () => {
      console.log('🚀 开始初始化数据...');
      setInitError(false);
      try {
        const [loadedUser] = await Promise.all([loadUserInfo(), loadPermissions()]);
        if (!loadedUser?.id) {
          setInitError(true);
          return;
        }
        const defaultTab = getDefaultTabKey();
        if (defaultTab) {
          setSelectedKey(defaultTab);
        }
        console.log('✅ 数据初始化完成');
      } catch (error) {
        console.error('数据初始化失败:', error);
        setInitError(true);
      } finally {
        setIsLoaded(true);
      }
    };

    initializeData();
  }, []);

  const AGENT_LIST_PAGE_SIZE = 100;

  const getAgentListCacheKey = (userId) => (
    userId ? `${CACHE_CONFIG.AGENT_LIST.key}_${userId}` : CACHE_CONFIG.AGENT_LIST.key
  );

  const findAgentRelation = (relations, agentId) => (
    relations.find((relation) => {
      const relationAgentId = relation.agent?.id ?? relation.agent;
      return String(relationAgentId) === String(agentId);
    })
  );

  // 智能体语言匹配函数：优先选已开通版本，再按语言匹配
  const findBestMatchingAgent = (agents, currentLanguage) => {
    if (!agents || agents.length === 0) return null;

    const pickByLanguage = (candidates) => (
      candidates.find((agent) => agent.language === currentLanguage)
      || candidates.find((agent) => agent.language === 'zh-hans')
      || candidates.find((agent) => agent.language === 'en')
      || candidates[0]
    );

    const activeAgents = agents.filter((agent) => !agent.expired);
    if (activeAgents.length > 0) {
      return pickByLanguage(activeAgents);
    }

    return pickByLanguage(agents);
  };

  // 按apikey分组智能体，每个apikey只显示一个最佳匹配的智能体
  const groupAgentsByApikey = (agents, currentLanguage) => {
    if (!agents || agents.length === 0) return [];
    
    // 按apikey分组（无 apikey 时按 id 独立分组，避免多个智能体被合并成一个）
    const groupedByApikey = {};
    agents.forEach(agent => {
      const groupKey = agent.apikey || `__no_apikey_${agent.id}`;
      if (!groupedByApikey[groupKey]) {
        groupedByApikey[groupKey] = [];
      }
      groupedByApikey[groupKey].push(agent);
    });
    
    // 为每个apikey组选择最佳匹配的智能体
    const bestAgents = [];
    Object.values(groupedByApikey).forEach(apikeyAgents => {
      const bestAgent = findBestMatchingAgent(apikeyAgents, currentLanguage);
      if (bestAgent) {
        bestAgents.push(bestAgent);
      }
    });
    
    return bestAgents;
  };

  // 获取智能体列表（带缓存）
  const fetchAgents = async () => {
    console.log('🔍 fetchAgents 函数被调用');
    
    // 检查是否有智能体模块权限
    if (!permissionManager.hasModulePermission('agent')) {
      console.log('❌ 没有智能体权限，跳过加载');
      return;
    }

    console.log('✅ 有智能体权限，开始获取智能体列表...');
    
    const agentListCacheKey = getAgentListCacheKey(userInfo?.id);

    // 尝试从缓存获取（按用户隔离，避免切换账号后显示错误权限）
    const cachedAgentData = cacheUtils.getCache(agentListCacheKey);
    if (cachedAgentData && userInfo && String(cachedAgentData.userId) === String(userInfo.id)) {
      console.log('✅ 从缓存加载智能体列表');
      
      // 恢复原始智能体数据
      originalAgentsRef.current = cachedAgentData.originalAgents;
      
      // 获取当前语言
      const currentLanguage = localStorage.getItem('i18nextLng') || 'zh-hans';
      
      // 重新应用分组逻辑
      const regroupedAgents = groupAgentsByApikey(cachedAgentData.originalAgents, currentLanguage);
      setAgentList(regroupedAgents);
      
      // 处理智能体选择逻辑
      handleAgentSelection(regroupedAgents, currentLanguage);
      
      return;
    }

    // 从服务器获取数据
    setIsLoadingAgents(true);
    try {
      // 获取当前用户
      const userData = localStorage.getItem('user_info');
      const userId = userData ? JSON.parse(userData).id : null;

      // 超级管理员和管理员可以访问所有智能体
      const isAdmin = userInfo && (userInfo.role === 'superadmin' || userInfo.role === 'academic_admin' || userInfo.role === 'manager');
      
      let allAgents = [];
      let relations = [];

      // 获取用户-智能体关系（所有用户都需要，拉取全量避免分页截断）
      if (userId) {
        const relRes = await axios.get('/api/agent-user-relations/', {
          params: { user: userId, page_size: AGENT_LIST_PAGE_SIZE },
        });
        relations = Array.isArray(relRes.data)
          ? relRes.data
          : (relRes.data?.results || []);
        console.log('用户智能体关系:', relations);
      }

      // 所有用户都获取所有智能体，然后在处理时判断权限状态
      const response = await axios.get('/api/agents/', {
        params: { page_size: AGENT_LIST_PAGE_SIZE },
      });
      allAgents = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);

      // 组装智能体列表，判断有效期
      const now = new Date();
      console.log('开始组装智能体列表，用户角色:', isAdmin ? '管理员' : '普通用户');
      console.log('智能体关系:', relations);
      console.log('所有智能体数量:', allAgents.length);
      
      const agents = allAgents.map(agent => {
        const rel = findAgentRelation(relations, agent.id);
        let expired = false;
        let expireType = ''; // 使用类型标识符而不是文本
        let expireText = '';
        
        if (isAdmin) {
          // 管理员可以访问所有智能体，但需要检查关系状态
          if (rel) {
            if (rel.end_time && new Date(rel.end_time) < now) {
              // 有关系但已过期
              expired = true;
              expireType = 'expired';
              expireText = t('common.expiredContactAdmin');
            } else {
              // 有关系且未过期
              expired = false;
              expireType = 'active';
              expireText = '';
            }
          } else {
            // 管理员看到没有关系的智能体，标记为未开通
            expired = true;
            expireType = 'no_permission';
            expireText = t('common.notOpenedContactAdmin');
          }
        } else {
          // 普通用户检查关系有效期
          if (rel) {
            if (rel.end_time && new Date(rel.end_time) < now) {
              expired = true;
              expireType = 'expired';
              expireText = t('common.expiredContactAdmin');
            }
          } else {
            // 普通用户没有关系，标记为无权限
            expired = true;
            expireType = 'no_permission';
            expireText = t('common.noPermissionContactAdmin');
          }
        }
        
        const agentWithStatus = {
          ...agent,
          id: agent.id.toString(),
          expired,
          expireType,
          expireText,
        };
        
        console.log(`智能体 ${agent.name} (ID: ${agent.id}):`, {
          hasRelation: !!rel,
          expired,
          expireType,
          relation: rel,
          isAdmin
        });
        
        return agentWithStatus;
      });

      console.log('原始智能体列表:', agents);
      
      // 保存原始智能体数据到ref中
      originalAgentsRef.current = agents;
      
      // 缓存智能体数据（按用户隔离）
      cacheUtils.setCache(agentListCacheKey, {
        originalAgents: agents,
        relations: relations,
        userId: userInfo?.id,
        userRole: userInfo?.role,
        timestamp: Date.now()
      }, CACHE_CONFIG.AGENT_LIST.ttl);
      
      // 获取当前语言
      const currentLanguage = localStorage.getItem('i18nextLng') || 'zh-hans';
      
      // 按apikey分组，每个apikey只显示一个最佳匹配的智能体
      const groupedAgents = groupAgentsByApikey(agents, currentLanguage);
      console.log('按apikey分组后的智能体列表:', groupedAgents);
      
      setAgentList(groupedAgents);
      
      // 处理智能体选择逻辑
      handleAgentSelection(groupedAgents, currentLanguage);
      

      
    } catch (error) {
      console.error('获取智能体列表失败:', error);
      // 如果获取失败，说明没有权限，设置空列表
      setAgentList([]);
      setCurrentAgentId(null);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  // 处理智能体选择逻辑
  const handleAgentSelection = (groupedAgents, currentLanguage) => {
    // 优先选择有权限的智能体
    const availableAgents = groupedAgents.filter(a => !a.expired);
    
    // 根据语言匹配选择最佳智能体，只在没有选中智能体且用户未手动选择时才自动选择
    const bestAgent = availableAgents.length > 0 ? findBestMatchingAgent(availableAgents, currentLanguage) : null;
    
    if (bestAgent && (!currentAgentId || currentAgentId === null) && !hasUserSelectedAgent) {
      console.log('🎯 首次加载，自动选择最佳匹配智能体:', bestAgent.name);
      setCurrentAgentId(bestAgent.id);
    } else if (currentAgentId && !hasUserSelectedAgent) {
      // 检查当前选中的智能体是否在新的分组列表中，只在用户未手动选择时
      const currentAgentExists = groupedAgents.find(a => a.id === currentAgentId);
      if (!currentAgentExists && bestAgent) {
        console.log('🎯 当前智能体不在新列表中，切换到最佳匹配智能体:', bestAgent.name);
        setCurrentAgentId(bestAgent.id);
      } else if (currentAgentExists) {
        console.log('✅ 当前智能体仍在新列表中，保持选择:', currentAgentId);
      }
    } else if (hasUserSelectedAgent) {
      console.log('👤 用户已手动选择智能体，保持用户选择:', currentAgentId);
    }
    
    // 如果是第一次访问智能体tab且没有选中智能体，选择第一个可用的智能体
    if (selectedKey === 'robot' && hasVisitedRobotTab && !currentAgentId && availableAgents.length > 0) {
      const firstAvailableAgent = availableAgents[0];
      console.log('🎯 第一次访问智能体tab，自动选择第一个可用智能体:', firstAvailableAgent.name);
      setCurrentAgentId(firstAvailableAgent.id);
    }
  };



  // 获取智能体列表
  useEffect(() => {
    console.log('🚀 useEffect 被触发，isLoaded:', isLoaded);
    if (isLoaded) {
      fetchAgents();
    }
  }, [isLoaded, userInfo]); // 依赖isLoaded和userInfo，确保用户信息加载后再获取智能体

  // 监听语言变化，重新应用分组逻辑并切换智能体
  useEffect(() => {
    if (originalAgentsRef.current.length > 0) {
      const currentLanguage = i18n.language || 'zh-hans';
      console.log('🌍 语言切换检测到，当前语言:', currentLanguage);
      
      // 使用保存的原始智能体数据重新分组
      const regroupedAgents = groupAgentsByApikey(originalAgentsRef.current, currentLanguage);
      console.log('🔄 重新分组后的智能体列表:', regroupedAgents);
      
      setAgentList(regroupedAgents);
      
      // 检查当前选中的智能体是否在新的分组列表中，只在用户未手动选择时
      const currentAgentExists = regroupedAgents.find(a => a.id === currentAgentId);
      
      if (!currentAgentExists && !hasUserSelectedAgent) {
        // 如果当前选中的智能体不在新列表中且用户未手动选择，才选择最佳匹配的智能体
        const bestAgent = findBestMatchingAgent(regroupedAgents, currentLanguage);
        if (bestAgent) {
          console.log('🎯 当前智能体不在新列表中，切换到最佳匹配智能体:', bestAgent.name);
          setCurrentAgentId(bestAgent.id);
        }
      } else if (currentAgentExists) {
        console.log('✅ 当前智能体仍在新列表中，保持选择:', currentAgentId);
      } else if (hasUserSelectedAgent) {
        console.log('👤 用户已手动选择智能体，即使不在新列表中也保持选择:', currentAgentId);
      }
    }
  }, [i18n.language]); // 只监听i18n语言变化，移除currentAgentId依赖

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (!userInfo) return t('common.student');
    return userInfo.username || userInfo.first_name || userInfo.last_name || t('common.student');
  };

  // 安全的base64编码函数，支持中文字符
  const safeBtoa = (str) => {
    try {
      return btoa(unescape(encodeURIComponent(str)));
    } catch (e) {
      // 如果编码失败，返回一个默认的头像
      return btoa(unescape(encodeURIComponent(`<svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#1890FF;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#13C2C2;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="200" height="200" rx="100" fill="url(#grad1)"/>
        <text x="100" y="130" font-family="Arial, sans-serif" font-size="100" font-weight="bold" text-anchor="middle" fill="white">${t('common.student').charAt(0)}</text>
      </svg>`)));
    }
  };

  // 获取用户头像
  const getUserAvatar = () => {
    if (!userInfo) {
      const firstName = '学';
      return `data:image/svg+xml;base64,${safeBtoa(`<svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#1890FF;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#13C2C2;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="200" height="200" rx="100" fill="url(#grad1)"/>
        <text x="100" y="130" font-family="Arial, sans-serif" font-size="100" font-weight="bold" text-anchor="middle" fill="white">${firstName}</text>
      </svg>`)}`;
    }
    
    if (userInfo.avatar) return userInfo.avatar;
    
    // 获取用户姓名的第一个字符
    const displayName = userInfo.first_name || userInfo.last_name || userInfo.username || t('common.student').charAt(0);
    const firstName = displayName.charAt(0).toUpperCase();
    
    // 生成彩色渐变头像
    const gradients = [
      { id: 'grad1', colors: ['#1890FF', '#13C2C2'] },
      { id: 'grad2', colors: ['#52C41A', '#73D13D'] },
      { id: 'grad3', colors: ['#722ED1', '#B37FEB'] },
      { id: 'grad4', colors: ['#FA8C16', '#FFC53D'] },
      { id: 'grad5', colors: ['#F5222D', '#FF7875'] },
      { id: 'grad6', colors: ['#13C2C2', '#36CFC9'] },
      { id: 'grad7', colors: ['#EB2F96', '#F759AB'] },
      { id: 'grad8', colors: ['#FAAD14', '#FFD666'] }
    ];
    const gradient = gradients[displayName.charCodeAt(0) % gradients.length];
    
    return `data:image/svg+xml;base64,${safeBtoa(`<svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="${gradient.id}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${gradient.colors[0]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${gradient.colors[1]};stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="200" height="200" rx="100" fill="url(#${gradient.id})"/>
      <text x="100" y="130" font-family="Arial, sans-serif" font-size="100" font-weight="bold" text-anchor="middle" fill="white">${firstName}</text>
    </svg>`)}`;
  };

  // 过滤有权限的菜单项
  const filteredMenuItems = getAvailableMenuItems();

  // 权限加载后，自动选中第一个有权限的 tab
  useEffect(() => {
    if (isLoaded && filteredMenuItems.length > 0) {
      // 如果当前 selectedKey 没有权限，自动切换到第一个有权限的 tab
      const currentHasPermission = filteredMenuItems.some(item => item.key === selectedKey);
      if (!currentHasPermission) {
        setSelectedKey(filteredMenuItems[0].key);
      }
    }
  }, [isLoaded, filteredMenuItems.length]);

  // agentList 分组
  console.log('智能体分组 - 原始列表:', agentList);
  const openedAgents = agentList.filter(a => !a.expired);
  const expiredAgents = agentList.filter(a => a.expired && a.expireType === 'expired');
  const noPermissionAgents = agentList.filter(a => a.expired && a.expireType === 'no_permission');
  
  // 监听openedAgents变化，在第一次访问智能体tab时自动选择第一个可用智能体
  useEffect(() => {
    if (selectedKey === 'robot' && hasVisitedRobotTab && !currentAgentId && openedAgents.length > 0 && !hasUserSelectedAgent) {
      const firstAvailableAgent = openedAgents[0];
      console.log('🎯 智能体列表更新，自动选择第一个可用智能体:', firstAvailableAgent.name);
      setCurrentAgentId(firstAvailableAgent.id);
    }
  }, [openedAgents, selectedKey, hasVisitedRobotTab, currentAgentId, hasUserSelectedAgent]);
  
  console.log('智能体分组结果:', {
    opened: openedAgents.length,
    expired: expiredAgents.length,
    noPermission: noPermissionAgents.length,
    openedAgents: openedAgents.map(a => ({ id: a.id, name: a.name, expired: a.expired, expireType: a.expireType })),
    expiredAgents: expiredAgents.map(a => ({ id: a.id, name: a.name, expired: a.expired, expireType: a.expireType })),
    noPermissionAgents: noPermissionAgents.map(a => ({ id: a.id, name: a.name, expired: a.expired, expireType: a.expireType }))
  });

  const renderCenteredStatus = (icon, title, subtitle) => (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 16,
      color: '#666',
      minHeight: '60vh',
    }}>
      <div style={{ fontSize: 48 }}>{icon}</div>
      <div style={{ fontSize: 20 }}>{title}</div>
      {subtitle && <div>{subtitle}</div>}
    </div>
  );

  if (!isLoaded) {
    return (
      <div className="school-root">
        {renderCenteredStatus('🔄', t('common.loading'), t('common.loadingPermissionInfo'))}
      </div>
    );
  }

  if (initError || !userInfo) {
    return (
      <div className="school-root">
        <div className="school-header">
          <div className="school-header-left" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/logo.png" alt="logo" style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginRight: 16, display: 'block' }} />
            <img src="/assets/logo-text.png" alt="blooart CAMPUS" style={{ height: 32, display: 'block' }} />
          </div>
        </div>
        {renderCenteredStatus('⚠️', t('common.error'), t('common.contactAdmin'))}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <button
            type="button"
            onClick={() => {
              cacheUtils.clearAllCache();
              localStorage.removeItem('user_info');
              window.location.reload();
            }}
            style={{ marginRight: 12, background: theme.PRIMARY_COLOR, color: '#231a17', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer' }}
          >
            {t('common.refresh')}
          </button>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user_info');
              window.location.replace('/login');
            }}
            style={{ background: theme.PRIMARY_COLOR, color: '#231a17', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer' }}
          >
            {t('common.logout')}
          </button>
        </div>
      </div>
    );
  }

  if (filteredMenuItems.length === 0) {
    return (
      <div className="school-root">
        <div className="school-header">
          <div className="school-header-left" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/logo.png" alt="logo" style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginRight: 16, display: 'block' }} />
            <img src="/assets/logo-text.png" alt="blooart CAMPUS" style={{ height: 32, display: 'block' }} />
          </div>
          <div className="school-header-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button type="button" onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user_info'); window.location.replace('/login'); }} style={{ background: theme.PRIMARY_COLOR, color: '#231a17', border: 'none', borderRadius: 6, padding: '4px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{t('common.logout')}</button>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#666', minHeight: '60vh' }}>
          <div style={{ fontSize: 48 }}>🚫</div>
          <div style={{ fontSize: 20 }}>{t('common.insufficientPermissions')}</div>
          <div>{t('common.contactAdmin')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="school-root">
      {/* 顶部栏 */}
      <div className="school-header">
        <div className="school-header-left" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/logo.png" alt="logo" style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginRight: 16, display: 'block' }} />
          <img src="/assets/logo-text.png" alt="blooart CAMPUS" style={{ height: 32, display: 'block' }} />
        </div>
        <div className="school-header-right" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* 语言选择器 */}
          <LanguageSelector 
            type="dropdown" 
            size="small"
          />
          <span style={{ color: theme.PRIMARY_COLOR, fontSize: '14px', fontWeight: '600' }}>
            {t('common.welcome')}：{getUserDisplayName()}
          </span>

          <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }} style={{ background: theme.PRIMARY_COLOR, color: '#231a17', border: 'none', borderRadius: 6, padding: '4px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'background 0.2s', height: '28px', lineHeight: '1' }}>{t('common.logout')}</button>
        </div>
      </div>
      {/* 主体区域 */}
      <div className="school-main">
        {/* 左侧主侧边栏 */}
        <div className="school-main-sidebar">
          <div className="school-sidebar-profile">
            <img className="qa-avatar" src={getUserAvatar()} alt="avatar" />
            <div className="qa-username">{getUserDisplayName()}</div>
          </div>
          <div className="school-menu-icons">
            {filteredMenuItems.map(item => (
              <div
                key={item.key}
                className={`school-menu-icon${selectedKey === item.key ? ' school-menu-icon-active' : ''}`}
                onClick={() => handleTabChange(item.key)}
                title={item.label}
              >
                {item.icon}
              </div>
            ))}
          </div>
        </div>
        {/* 右侧内容区：问答tab显示ChatPanel，云空间等tab显示其它内容 */}
        <div className="school-content-area" style={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>
          {selectedKey === 'qa' && permissionManager.hasModulePermission('chat') && userInfo && (
            <ChatPanelDifyChatflowApi 
              apiUrl="https://kb.blooart.cn/v1/chat-messages"
              apiKey="app-hhYd5HqseerqmEzPtQ2lCPFv"
              currentAgent={{
                id: 'school_qa_agent',
                name: t('common.schoolQA'),
                avatar: '/assets/chat-question.png',
                welcomeText: t('common.welcomeToSchoolQA')
              }}
              userInfo={userInfo}
              namespace="school_qa"
              showModelSelection={true}
              showKnowledgeBase={true}
              showImageUpload={false}
              showDynamicForm={false}
            />
          )}
          {selectedKey === 'qa' && permissionManager.hasModulePermission('chat') && !userInfo && (
            renderCenteredStatus('🔄', t('common.loading'), t('common.loadingPermissionInfo'))
          )}
          {selectedKey === 'folder' && permissionManager.hasModulePermission('document') && (
            <div style={{ width: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #e0e0e0', padding: '24px 32px', border: '1px solid #eee', flex: 1, minWidth: 0 }}>
              <DocumentManage />
            </div>
          )}
          {selectedKey === 'robot' && permissionManager.hasModulePermission('agent') && (
            <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
              {isLoadingAgents ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  gap: 12,
                  color: '#64748b',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)',
                }}
                >
                  <ThunderboltOutlined style={{ fontSize: 32, color: '#6366f1' }} spin />
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{t('common.loadingAgentList')}</div>
                </div>
              ) : agentList.length > 0 ? (
                <div style={{ display: 'flex', height: '100%' }}>
                  {/* 右侧聊天面板（含统一侧边栏：智能体 + 对话历史） */}
                  <div style={{ flex: 1, height: '100%', position: 'relative' }}>
                    {/* 为每个智能体创建独立的聊天面板，只显示当前选中的 */}
                    {agentList.map(agent => (
                      <div
                        key={agent.id}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: currentAgentId === agent.id ? 'block' : 'none',
                          zIndex: currentAgentId === agent.id ? 1 : 0
                        }}
                      >
                        <AgentChatPanel
                          agent={agent}
                          userInfo={userInfo}
                          t={t}
                          agentSidebar={currentAgentId === agent.id ? (
                            <AgentSidebar
                              embedded
                              openedAgents={openedAgents}
                              expiredAgents={expiredAgents}
                              noPermissionAgents={noPermissionAgents}
                              currentAgentId={currentAgentId}
                              onAgentClick={handleAgentClick}
                              t={t}
                            />
                          ) : null}
                        />
                      </div>
                    ))}
                    {currentAgent && currentAgent.expired && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(255,255,255,0.98)', zIndex: 10,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                      }}>
                        <div style={{ 
                          fontSize: 64, 
                          marginBottom: 16,
                          opacity: 0.8
                        }}>
                          {currentAgent.expireType === 'expired' ? '⏰' : '🔒'}
                        </div>
                        <div style={{ 
                          color: currentAgent.expireType === 'expired' ? '#faad14' : '#f5222d', 
                          fontSize: 24, 
                          fontWeight: 600,
                          textAlign: 'center',
                          marginBottom: 12
                        }}>
                          {currentAgent.expireType === 'expired' ? t('common.expired') : t('common.noPermission')}
                        </div>
                        <div style={{ 
                          color: '#666', 
                          fontSize: 16,
                          textAlign: 'center',
                          maxWidth: 400,
                          lineHeight: 1.5
                        }}>
                          {currentAgent.expireText || (currentAgent.expireType === 'expired' ? t('common.expiredContactAdmin') : t('common.noPermissionContactAdmin'))}
                        </div>
                        <div style={{ 
                          marginTop: 24,
                          padding: '12px 24px',
                          background: currentAgent.expireType === 'expired' ? '#faad14' : '#f5222d',
                          color: 'white',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                        onClick={() => {
                          // 可以在这里添加联系管理员的逻辑
                          console.log('用户点击了联系管理员按钮');
                        }}
                        >
                          {t('common.contactAdmin')}
                        </div>
                      </div>
                    )}
                    {!currentAgent && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%',
                        flexDirection: 'column',
                        gap: 16,
                        color: '#666'
                      }}>
                        <AgentAvatar agent={{ id: 'select', name: 'AI' }} size={56} />
                        <div style={{ fontSize: 18 }}>{t('common.selectAgent')}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                  {t('common.noAvailableAgents')}
                </div>
              )}
            </div>
          )}
          
          {/* 权限不足提示 */}
          {selectedKey === 'qa' && !permissionManager.hasModulePermission('chat') && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🚫</div>
              <div style={{ fontSize: 24 }}>{t('common.insufficientPermissions')}</div>
              <div>{t('common.noChatPermission')}</div>
            </div>
          )}
          {selectedKey === 'folder' && !permissionManager.hasModulePermission('document') && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🚫</div>
              <div style={{ fontSize: 24 }}>{t('common.insufficientPermissions')}</div>
              <div>{t('common.noDocumentPermission')}</div>
            </div>
          )}
          {selectedKey === 'robot' && !permissionManager.hasModulePermission('agent') && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🚫</div>
              <div style={{ fontSize: 24 }}>{t('common.insufficientPermissions')}</div>
              <div>{t('common.noAgentPermission')}</div>
            </div>
          )}
          
          {/* 加载中状态 */}
          {selectedKey === 'qa' && !permissionManager.isLoaded && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🔄</div>
              <div style={{ fontSize: 24 }}>{t('common.loading')}</div>
              <div>{t('common.loadingPermissionInfo')}</div>
            </div>
          )}
          {selectedKey === 'folder' && !permissionManager.isLoaded && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🔄</div>
              <div style={{ fontSize: 24 }}>{t('common.loading')}</div>
              <div>{t('common.loadingPermissionInfo')}</div>
            </div>
          )}
          {selectedKey === 'robot' && !permissionManager.isLoaded && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 16,
              color: '#666'
            }}>
              <div style={{ fontSize: 48 }}>🔄</div>
              <div style={{ fontSize: 24 }}>{t('common.loading')}</div>
              <div>{t('common.loadingPermissionInfo')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolDashboard;
